import { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPenToSquare,
  faFloppyDisk,
  faRotateLeft,
  faRotateRight,
  IconDefinition,
  faCog,
  faBanBug,
  faLocationPlus,
  faCirclePlus,
  faDrawPolygon,
  faLocationPen,
  faRoute,
  faTrashCan,
  faPalette,
  faLink,
} from '@fortawesome/pro-regular-svg-icons';
import cx from 'classnames';
import { ActionCreators } from 'redux-undo';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import type { Placement } from "@floating-ui/react";

import {
  toggleEdit,
  isEditingSelector,
  isEditingSettingsSelector,
  toggleEditingSettings,
  saveAPI,
  apiSavingStatusSelector,
  useAppDispatch,
  useAppSelector,
  canUndoSelector,
  canRedoSelector,
  mapNeedsSaveSelector,
  setEditedSpaceId,
  mapSelector,
  editModeSelector,
  setEditMode,
  clearAllPaths,
  hasPathsOnCurrentLayerSelector,
  activeImageSelector,
  setActiveImage,
  setMarkerEditingSpaceId,
} from '../../store';
import eventBus from '../../eventBus';

import { Tooltip, TooltipTrigger, TooltipContent } from '../Tooltip';
import { useRootElementEvents } from '../../hooks/useRootElementEvents';
import rootElement from '../../rootElement';

import './EditTools.scss';

interface IconProps {
  icon: IconDefinition;
  id?: string;
  tooltip?: string;
  active?: boolean;
  pulse?: boolean;
  success?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  tooltipPlacement: Placement;
}

function Icon({ icon, id, tooltip, active, pulse, success, onClick, disabled, tooltipPlacement }: IconProps) {
  return (
    <Tooltip placement={tooltipPlacement}>
      <TooltipTrigger>
        <div
          id={id}
          onClick={() => !disabled && onClick && onClick()}
          className={cx('icon', { active, success, disabled })}
        >
          <FontAwesomeIcon
            icon={icon}
            size="xl"
            className={cx({ pulse })}
          />
        </div>
      </TooltipTrigger>
      {tooltip && (
        <TooltipContent>
          {tooltip}
        </TooltipContent>
      )}
    </Tooltip>
  );
}

interface EditToolsProps {
  setIsSidebarOpen: Function;
  isThemeSidebarOpen: boolean;
  setIsThemeSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export function EditTools({
  setIsSidebarOpen,
  isThemeSidebarOpen,
  setIsThemeSidebarOpen,
}: EditToolsProps) {
  const iMap = useAppSelector(mapSelector);
  const dispatch = useAppDispatch();
  const isEditing = useAppSelector(isEditingSelector);
  const editMode = useAppSelector(editModeSelector);
  const isEditingSettings = useAppSelector(isEditingSettingsSelector);
  const savingStatus = useAppSelector(apiSavingStatusSelector);
  const canUndo = useAppSelector(canUndoSelector);
  const canRedo = useAppSelector(canRedoSelector);
  const needsSave = useAppSelector(mapNeedsSaveSelector);
  const hasIndoorPaths = useAppSelector(hasPathsOnCurrentLayerSelector);
  const canSave = needsSave && savingStatus !== 'saving';
  const activeImage = useAppSelector(activeImageSelector);

  const handleSave = useCallback(() => {
    if (savingStatus === 'saving') return;

    const flushState = { didFlush: false, blocked: false };
    eventBus.emit('flush-pending-edits', flushState);

    if (!flushState.blocked && (canSave || flushState.didFlush)) {
      dispatch(saveAPI());
    }
  }, [canSave, dispatch, savingStatus]);

  const undo = useCallback(() => {
    dispatch(ActionCreators.undo());
    dispatch(setEditedSpaceId(null));
  }, [dispatch]);
  const redo = useCallback(() => {
    dispatch(ActionCreators.redo());
    dispatch(setEditedSpaceId(null));
  }, [dispatch]);

  const [showReactQueryDevtools, setShowReactQueryDevtools] = useState(false);
  const [tooltipPlacement, setTooltipPlacement] = useState<Placement>('right');
  const editToolsRef = useRef(null);

  // Observe sidebar size to decide where to place tooltips
  useEffect(() => {
    const editToolsDiv = editToolsRef.current;
    if (!editToolsDiv) return;
    const resizeObserver = new ResizeObserver((e) => {
      const { inlineSize, blockSize } = e[0].contentBoxSize[0];
      setTooltipPlacement(
        blockSize > inlineSize ? 'right' : 'top'
      );
    });
    resizeObserver.observe(editToolsDiv);
    return () => {
      resizeObserver.unobserve(editToolsDiv);
    };
  }, [editToolsRef]);

  useRootElementEvents(['keydown'], (e: KeyboardEvent) => {
    const { target } = e;

    if (target instanceof HTMLElement) {
      if (target.className === 'rsw-ce') return;
      if (target instanceof HTMLInputElement) return;
      if (target instanceof HTMLTextAreaElement) return;
      if (target.isContentEditable) return;
    }
    if (e.ctrlKey) {
      if (e.key === 'z') {
        undo();
      }

      if (e.key === 'y' || e.key === 'Z') {
        redo();
      }

      if (e.key === 's') {
        e.preventDefault();
        handleSave();
      }

      if (e.key === 'a') {
        e.preventDefault();
        eventBus.emit('select-all');
      }
    } else if (e.key === 'e') {
      toggleEditMode();
    }
  });

  const toggleEditMode = useCallback(() => {
    // Open sidebar when activating Edit mode
    if (!isEditing) setIsSidebarOpen(true);
    dispatch(toggleEdit());
  }, [dispatch, isEditing, setIsSidebarOpen]);

  return (
    <div
      ref={editToolsRef}
      id="edit-tools"
      className="edit-bar"
    >
      <nav>
        <ul>
          <li>
            <Icon
              icon={faPenToSquare}
              tooltip="Edit Mode [E]"
              active={isEditing}
              onClick={toggleEditMode}
              tooltipPlacement={tooltipPlacement}
            />
          </li>
          {isEditing && (
            <>
              {activeImage && activeImage.duplicateOf ? (
                <li>
                  <Icon
                    icon={faLink}
                    tooltip="Currently editing a duplicate image. Edit the original image to make changes."
                    active={false}
                    onClick={() => {
                      dispatch(setActiveImage(activeImage.duplicateOf));
                      // close Editor when changing Layer
                      dispatch(setEditedSpaceId(null));
                      // Close marker editor
                      dispatch(setMarkerEditingSpaceId(null));
                    }}
                    tooltipPlacement={tooltipPlacement}
                  />
                </li>
              ) : (
                <>
                  <li>
                    <Icon
                      icon={faRotateLeft}
                      tooltip="Undo [Ctrl+Z]"
                      active={false}
                      onClick={undo}
                      disabled={!canUndo || savingStatus === 'saving'}
                      tooltipPlacement={tooltipPlacement}
                    />
                  </li>
                  <li>
                    <Icon
                      icon={faRotateRight}
                      tooltip="Redo [Ctrl+Y]"
                      active={false}
                      onClick={redo}
                      disabled={!canRedo || savingStatus === 'saving'}
                      tooltipPlacement={tooltipPlacement}
                    />
                  </li>
                  <li>
                    <Icon
                      icon={faLocationPlus}
                      tooltip="Place Space Pin"
                      active={editMode === 'pin'}
                      onClick={() => {
                        editMode === 'pin'
                          ? dispatch(setEditMode(''))
                          : dispatch(setEditMode('pin'));
                      }}
                      tooltipPlacement={tooltipPlacement}
                    />
                  </li>
                  {iMap.allowLabels && (
                    <li>
                      <Icon
                        icon={faCirclePlus}
                        tooltip="Place Label"
                        active={editMode === 'label'}
                        onClick={() => {
                          editMode === 'label'
                            ? dispatch(setEditMode(''))
                            : dispatch(setEditMode('label'));
                        }}
                        tooltipPlacement={tooltipPlacement}
                      />
                    </li>
                  )}
                  {activeImage?.type === 'Image' && (
                    <li>
                      <Icon
                        icon={faDrawPolygon}
                        tooltip="Draw / Edit Overlays"
                        active={editMode === 'overlay'}
                        onClick={() => {
                          editMode === 'overlay'
                            ? dispatch(setEditMode(''))
                            : dispatch(setEditMode('overlay'));
                        }}
                        tooltipPlacement={tooltipPlacement}
                      />
                    </li>
                  )}
                  <li>
                    <Icon
                      icon={faLocationPen}
                      tooltip="Edit Space Pin"
                      active={editMode === 'marker'}
                      onClick={() => {
                        editMode === 'marker'
                          ? dispatch(setEditMode(''))
                          : dispatch(setEditMode('marker'));
                      }}
                      tooltipPlacement={tooltipPlacement}
                    />
                  </li>
                  <li>
                    <Icon
                      icon={faRoute}
                      tooltip={editMode === 'drawingPath' ? "Exit Path Drawing" : "Draw Navigation Paths"}
                      active={editMode === 'drawingPath'}
                      onClick={() => {
                        editMode === 'drawingPath'
                          ? dispatch(setEditMode(''))
                          : dispatch(setEditMode('drawingPath'));
                      }}
                      tooltipPlacement={tooltipPlacement}
                    />
                  </li>
                  {hasIndoorPaths && (
                    <li>
                      <Icon
                        icon={faTrashCan}
                        tooltip="Clear All Paths"
                        active={false}
                        onClick={() => {
                          if (window.confirm('Are you sure you want to delete all waypoints and paths? This cannot be undone.')) {
                            dispatch(clearAllPaths());
                          }
                        }}
                        tooltipPlacement={tooltipPlacement}
                      />
                    </li>
                  )}
                  <li>
                    <Icon
                      icon={faFloppyDisk}
                      tooltip={
                        savingStatus === 'saving'
                          ? 'Saving changes...'
                          : canSave
                            ? 'Save changes [Ctrl+S]'
                            : 'All changes saved'
                      }
                      active={false}
                      pulse={savingStatus === 'saving'}
                      success={savingStatus === 'success'}
                      onClick={handleSave}
                      disabled={!canSave}
                      tooltipPlacement={tooltipPlacement}
                    />
                  </li>
                </>
              )}
              {process.env.NODE_ENV === 'development'
                && <li className='edit-tools-devtools'>
                  <Icon
                    icon={faBanBug}
                    tooltip="TanStack Query DevTools"
                    active={showReactQueryDevtools}
                    onClick={() => setShowReactQueryDevtools(!showReactQueryDevtools)}
                    tooltipPlacement={tooltipPlacement}
                  />
                  {showReactQueryDevtools
                    && <ReactQueryDevtoolsPanel
                      shadowDOMTarget={rootElement instanceof ShadowRoot ? rootElement : undefined}
                      onClose={() => setShowReactQueryDevtools(false)}
                    />}
                </li>
              }
              <li className="edit-tools-theme">
                <Icon
                  icon={faPalette}
                  tooltip="Theme Editor"
                  active={isThemeSidebarOpen}
                  onClick={() => setIsThemeSidebarOpen(!isThemeSidebarOpen)}
                  disabled={false}
                  tooltipPlacement={tooltipPlacement}
                />
              </li>
              <li className="edit-tools-settings">
                <Icon
                  icon={faCog}
                  tooltip="Settings"
                  active={isEditingSettings}
                  onClick={() => {
                    // Open sidebar when opening map settings
                    if (!isEditingSettings) setIsSidebarOpen(true);
                    dispatch(toggleEditingSettings());
                  }}
                  disabled={false}
                  tooltipPlacement={tooltipPlacement}
                />
              </li>
            </>
          )}
        </ul>
      </nav>
    </div>
  );
}
